import React, { Suspense, lazy, useEffect, useMemo, useState } from 'react';
import { FLEET as FLEET_REGISTRY } from '../botIdentity';
import { sessionIso, API_BASE_URL } from '../liveTrading/foundation';
// SKIPPED CENSUS LEDGER on every bot card (God 2026-10-05 ~19:0x): Symbol · TF · Reason of THIS bot's skips.
const SkippedCensusLedger = lazy(() => import('../liveTrading/SkippedCensusLedger'));
import { canonSym } from './SuperChart/pineLibrary';
// Fill extraction lives in ONE module — the Granular Position Manager needs the
// same answer, and two copies of this become two different answers.
import { fillsOf } from '../tradeAnalytics';
import { usePaperLots, normSym } from '../lib/paperLots';

// ═══════════════════════════════════════════════════════════════════════════
// MASTER FLEET REGISTRY (God 2026-07-18) — the Rudra fleet (7 bots since
// 2026-08-08), permanently bound in ONE place. Everything below auto-syncs
// from the SERVER (Mongo `deployments` = SSOT + `tradeorders` ledger):
// symbols, TFs, status, mode, per-bot / per-symbol / per-TF PnL. ZERO manual
// entry — add a symbol to a deployment (auto-deploy pipeline or God) and it
// appears here on next poll.
// SAFETY: display + binding ONLY. This component NEVER flips status/mode —
// arming/LIVE stays God-gated (ghost auto-start 2026-07-13 = why).
//
// ── SMART HIERARCHY (God 2026-08-26) ──────────────────────────────────────
// The flat layout had run out of room. Every bot rendered its legs as one chip
// wall capped at 12 with a "+18 more" tail, so on a 30-leg bot most of the
// fleet was simply not on screen, and the twelve that were had no numbers on
// them. Three levels replace it:
//
//   L1  SYMBOL      one row: today's realised PnL, trade count, live M2M
//   L2  TIMEFRAME   expands from L1 — the (symbol × TF) legs that actually
//                   traded, in real time order
//   L3  METRICS     expands from L2 — total trades · live M2M · avg buy ·
//                   avg sell, for that one leg
//
// Collapsed by default, and it STAYS collapsed until clicked. An open position
// is marked (green rail, live dot, OPEN badge) but never expands itself.
//
// ── WHERE EACH NUMBER COMES FROM, AND WHY IT IS NOT COMPUTED HERE ─────────
// Live M2M is read VERBATIM from `/bridge/rag/positions`. God removed the
// client-side mark-to-market on 2026-08-03 so that this card and the Open
// Positions panel can never disagree — there is exactly one canonical
// server-side computation and the UI binds to it. This file does not multiply
// an LTP by a quantity anywhere, on purpose.
//
// That endpoint's leg rows carry no `timeframe`, so per-(symbol × TF) live P&L
// is obtained by JOINING each leg to its ledger row on the trade id
// (`TRD-…`, the same key `ledger_open_positions()` emits). The join moves the
// timeframe onto the leg; the P&L still comes from the server untouched.
// ═══════════════════════════════════════════════════════════════════════════

const API = 'https://api.jaiswalcapital.com';
const POSITIONS_URL = `${API}/bridge/rag/positions`;

const FLEET = FLEET_REGISTRY;

type Leg = { symbol: string; tf: string; enabled: boolean; pine: string };
type CellState = { on: boolean; pine: string };
type BotDep = { status: string; mode: string; maxLoss: number; legs: Leg[];
  /** KALI only: the bot is listed but not yet cleared for live execution. */
  pending?: boolean };

/** One (symbol × TF) leg's day. `avgBuy`/`avgSell` are null when no fill of
 *  that side happened today — an open trade has bought and not yet sold, and
 *  showing 0 there would read as "sold at zero". */
type CellStats = {
  trades: number;
  pnl: number;
  wins: number;
  losses: number;
  liveM2M: number | null;
  openLegs: number;
  /** Of `openLegs`, how many were entered on an EARLIER day. A Shool positional
   *  hold is the normal case; it is called out so a running M2M with no matching
   *  trade count today does not read as a bug. */
  carried: number;
  /** Of `openLegs`, how many the positions feed could actually PRICE. A leg the
   *  bridge has no LTP for is still open — its M2M is merely unknown, and
   *  unknown must never be rendered as ₹0. */
  pricedLegs: number;
  avgBuy: number | null;
  avgSell: number | null;
  buyQty: number;
  sellQty: number;
};

type SymStats = CellStats & { byTf: Record<string, CellStats> };
type BotStats = CellStats & { bySymbol: Record<string, SymStats> };

const POS = '#26a69a', NEG = '#ef5350', MUT = '#64748b';
const pnlColor = (v: number) => (v > 0 ? POS : v < 0 ? NEG : MUT);
// TFs read in time order, never alphabetically: "15m,15s,1m,1s,5m,5s" is the
// alphabetical answer and it is unreadable. An unknown TF sorts last.
const TF_ORDER = ['1s', '5s', '15s', '30s', '1m', '3m', '5m', '15m', '30m', '1h', '1H', '4h', '4H', '1d', '1D', '1W', '—'];
const tfRank = (tf: string) => { const i = TF_ORDER.indexOf(String(tf)); return i < 0 ? 998 : i; };
const fmt = (v: number) =>
  `${v < 0 ? '−' : v > 0 ? '+' : ''}₹${Math.abs(v).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
// Prices are NOT money-formatted: an option premium of 364.50 and a GOLD future
// at 162,840 are both prices, and rounding the premium to ₹365 throws away the
// paise that decide whether the fill was good.
const px = (v: number | null) =>
  v == null ? '—' : v.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// SESSION day, not calendar day (God 2026-09-24): from midnight to the 09:00
// bell the registry totalled a day that had not started and every bot read
// "0 trades · 0W/0L". The last trading day is the session until the bell.
const todayIst = () => sessionIso();

const emptyCell = (): CellStats => ({
  trades: 0, pnl: 0, wins: 0, losses: 0, liveM2M: null,
  openLegs: 0, carried: 0, pricedLegs: 0, avgBuy: null, avgSell: null, buyQty: 0, sellQty: 0,
});

/** Accumulate one row into a cell. Averages are finalised later from the
 *  running weighted sums, never by averaging averages. */
function addRow(c: CellStats & { _bs?: number; _ss?: number }, r: any, pnl: number) {
  c.trades += 1;
  c.pnl += pnl;
  if (pnl > 0) c.wins += 1; else if (pnl < 0) c.losses += 1;
  for (const f of fillsOf(r)) {
    if (f.side === 'BUY') { c._bs = (c._bs || 0) + f.price * f.qty; c.buyQty += f.qty; }
    else { c._ss = (c._ss || 0) + f.price * f.qty; c.sellQty += f.qty; }
  }
}

function finalise(c: CellStats & { _bs?: number; _ss?: number }) {
  c.avgBuy = c.buyQty > 0 ? (c._bs || 0) / c.buyQty : null;
  c.avgSell = c.sellQty > 0 ? (c._ss || 0) / c.sellQty : null;
  delete c._bs; delete c._ss;
}

/**
 * lane — which trading lane this registry belongs to (God 2026-08-26).
 *
 * THIS COMPONENT FETCHES ITS OWN DATA, which is exactly how it kept leaking
 * after the lane fix. Every OTHER panel on the page is handed a lane-filtered
 * array by LiveTrading, and the regression guard checks those props. This one
 * receives nothing and calls /deployments, /api/trades/ledger and the positions
 * endpoint itself, so filtering upstream could never reach it — the KALI page
 * went on rendering all seven PAPER bots with RUNNING/PAPER badges under
 * MASTER FLEET REGISTRY while the header above it correctly read 0 and 0.
 *
 * Defaults to 'paper': a registry that was not told its lane must never be the
 * real-money one.
 */
/**
 * The ledger keys this card reads. Everything else on the row is weight.
 *
 * `_id` rides along with `id` because the row identity has been spelled both
 * ways over the life of this ledger, and a card that silently loses its key
 * would re-mount every poll.
 */
const LEDGER_FIELDS = [
  'id', '_id', 'botId', 'symbol', 'optionSymbol', 'timeframe',
  'pnl', 'netPnl', 'status', 'dateIso', 'tradingMode', 'voided',
  'openQty', 'qty',
  // THE FILL PRICES (God 2026-09-24 00:56, "yaha data nahi aa rahi hay"). The
  // drill-down prices every leg through fillsOf(), which reads entryPrice /
  // exitPrice / entrySide / exitSide / direction - none of which this
  // projection asked for, so every cell read "trades counted but no priced
  // fill" and AVG BUY / AVG SELL stayed "—" while the ledger held the prices.
  'entryPrice', 'exitPrice', 'price', 'entrySide', 'side', 'exitSide', 'direction', 'quantity',
].join(',');

const FleetRegistryCards: React.FC<{ lane?: 'paper' | 'kali' }> = ({ lane = 'paper' }) => {
  // Global paper lots per symbol (Funds Hub -> Symbol Lots): shown on each symbol row
  // so the card says what size the server will fire (paper lane only).
  const paperLots = usePaperLots();
  // An absent tradingMode counts as PAPER — an unlabelled bot is never assumed
  // to be spending money. Same rule the LiveTrading lane filter uses.
  const LANE_MODE = lane === 'kali' ? 'LIVE' : 'PAPER';
  const inLane = (m: unknown) =>
    (String(m || 'PAPER').toUpperCase() === 'LIVE' ? 'LIVE' : 'PAPER') === LANE_MODE;

  const [deps, setDeps] = useState<Record<string, BotDep>>({});
  // Per-bot skipped counts from the census route (one fetch per lane, 60 s), and which bot's ledger is open.
  const [skipByBot, setSkipByBot] = useState<Record<string, number>>({});
  const [ledgerBot, setLedgerBot] = useState<{ id: string; name: string } | null>(null);
  useEffect(() => {
    let alive = true;
    const pull = async () => {
      try {
        const r = await fetch(`${API_BASE_URL}/api/signals/skipped-census?lane=${lane}&date=${sessionIso()}&t=${Date.now()}`,
          { credentials: 'include', cache: 'no-store', signal: AbortSignal.timeout(15000) });
        const j = await r.json();
        if (alive && r.ok && j?.status === 'success') setSkipByBot(j.by_bot || {});
      } catch { /* the pill keeps its last count */ }
    };
    pull();
    const iv = setInterval(pull, 60000);
    return () => { alive = false; clearInterval(iv); };
  }, [lane]);
  const [rows, setRows] = useState<any[]>([]);
  // "NO FILL" AND "NOT LOADED" ARE DIFFERENT THINGS (God 2026-09-21, "yaha data
  // missing hay live min avg buy avg sell"). Before this, a cell whose ledger
  // slice had not arrived rendered exactly like a cell that genuinely never
  // traded: AVG BUY "—", sub "no buy fill". Measured on the live book the same
  // hour, every one of the day's 24 traded cells HAD both averages (FIN_NIFTY
  // 1s: 11 trades, buy 201.16, sell 202.57) — so a "—" there is a loading
  // state wearing the clothes of an answer. Now it says which it is.
  const [ledgerLoaded, setLedgerLoaded] = useState(false);
  // leg id → the server's own running P&L. Never recomputed here.
  const [live, setLive] = useState<Record<string, { ltp: number | null; pnl: number }>>({});
  const [posOk, setPosOk] = useState<boolean | null>(null);
  // /deployments answered 401/403 (TP1 2026-09-26: login required). Said out loud,
  // not drawn as an endless "syncing..." (God 2026-09-27 screenshot).
  const [depsAuth, setDepsAuth] = useState<number>(0);
  const [ts, setTs] = useState('');
  // K3 (2026-10-03, "secen say data 0 ho jati hay"): a failed poll used to set
  // rows to [] and the whole card dropped to 0 for one cycle. A failed poll now
  // keeps the last good rows and says so; only a real answer replaces them.
  const [staleSince, setStaleSince] = useState('');
  const [posAgeS, setPosAgeS] = useState(0);

  // ── expansion state ──────────────────────────────────────────────────────
  // Keys are `${botId}|${symbol}` and `${botId}|${symbol}|${tf}`.
  // Which CARDS have their symbol list open (God 2026-08-26). Collapsed by
  // default: Sthambh went from 7 symbols to 29, and one card tall enough to run
  // past the viewport broke the grid — the cards after it were pushed into empty
  // space instead of flowing beside it. A card that is always the same height
  // collapsed is what keeps the fleet readable at a glance.
  const [openCard, setOpenCard] = useState<Set<string>>(() => new Set());
  const [openSym, setOpenSym] = useState<Set<string>>(() => new Set());
  const [openTf, setOpenTf] = useState<Set<string>>(() => new Set());
  // NOTHING opens a row but a click (God 2026-08-26). A symbol carrying a
  // position used to auto-expand, and with legs opening and closing through the
  // session that kept re-opening rows underneath the admin. An open position is
  // still marked — green rail, live dot, OPEN badge — it just no longer decides
  // what is expanded. Expansion is the reader's state, not the fleet's.

  const toggleCard = (id: string) => {
    const n = new Set(openCard);
    n.has(id) ? n.delete(id) : n.add(id);
    setOpenCard(n);
  };

  const toggle = (set: Set<string>, k: string, fn: (s: Set<string>) => void) => {
    const n = new Set(set);
    n.has(k) ? n.delete(k) : n.add(k);
    fn(n);
  };

  useEffect(() => {
    let dead = false;
    const load = async () => {
      try {
        const [dRes, lRes] = await Promise.all([
          fetch(`${API}/deployments`).then(r => {
            setDepsAuth(r.status === 401 || r.status === 403 ? r.status : 0);
            return r.json();
          }).catch(() => null),
          // Window the ledger to TODAY (God 2026-08-24). Unfiltered this pulled
          // every row the API holds — 1,581 full documents, 2.29 MB — on every
          // poll, and the card sat on "syncing…" for seconds while it downloaded
          // 1,360 rows it then threw away in the `dateIso !== today` filter.
          //
          // `openAlways=1` (added 2026-08-26) is what `ledger_open_positions()`
          // itself sends. Without it a still-open leg can be absent from the
          // window, and the card would show a live M2M for a position whose
          // trade count it had never counted.
          //
          // `fields=` (God 2026-08-28) — ask for the 13 keys this card reads,
          // not the 59 the row carries. Measured: 461 rows x ~1,560 bytes =
          // ~702 KB per poll, ~85% of it discarded here. It also lets the API
          // skip the scrip-master enrichment, which this card never reads.
          // God's report was "dashboard par data aane me bahut time lagta hai,
          // screenshot lete waqt data aaya hi nahi" — the panel was rendering
          // its empty state while a 7.6 s cold response was still in flight.
          // KEEP THIS LIST IN SYNC with what the reducer below actually reads;
          // a key added there and forgotten here arrives as undefined, which
          // reads as 0 rather than as an error.
          fetch(`${API}/api/trades/ledger?from=${todayIst()}&openAlways=1&limit=2000`
                + `&fields=${LEDGER_FIELDS}&t=${Date.now()}`,
                { cache: 'no-store' }).then(r => r.json()).catch(() => null),
        ]);
        if (dead) return;
        if (dRes?.deployments) {
          const next: Record<string, BotDep> = {};
          for (const f of FLEET) {
            const d = (dRes.deployments as any[]).find(x => x.id === f.id);
            if (!d) continue;
            // LANE GATE, NARROWED (God 2026-08-28). It used to drop the bot
            // entirely, so with the fleet 100% paper this registry drew NOTHING
            // on KALI. God's order is that KALI carry the same complete fleet as
            // DUMABATI and label what is not yet approved.
            //
            // So the gate moved off IDENTITY and onto MONEY: the bot is kept and
            // marked `pending`, while the LEDGER filter a few lines below is
            // untouched — that is the one that decides whose trades and P&L this
            // page is allowed to total, and it stays LIVE-only. Listing a bot and
            // banking its paper fills are two different claims; only the second
            // one was ever the 2026-08-26 defect.
            const _pending = !inLane(d.tradingMode);
            if (_pending && lane !== 'kali') continue;
            // God 2026-11-03: EVERY leg is listed, ON or OFF - a DISARMED cell (no Pine v6 / passport yet) is still a
            // cell of this bot and must show its symbol x TF; the chip on its TF row says ON / OFF and the pinned Pine.
            const legs: Leg[] = ((d.assets || []) as any[])
              .filter(a => !!a)
              .map(a => ({
                // canonSym, not a local spelling. `deployments` says "BANK NIFTY"
                // and the ledger says "BANK_NIFTY"; keying on the raw strings drew
                // BOTH as separate rows, so every index appeared twice — once with
                // the real numbers and once as a 0t · ₹0 ghost.
                // `symbol` before `baseSymbol` (God 2026-09-30, "NIFTY fake hai"):
                // the ledger keys on `symbol` (NIFTY_50, GOLD_FUT); baseSymbol is
                // the underlying (NIFTY, GOLD), so it drew a second empty row.
                symbol: canonSym(a.symbol || a.baseSymbol),
                tf: String(a.timeframe || ''), enabled: a.enabled !== false,
                pine: a.pinnedPine?.ver ? `v${a.pinnedPine.ver}` : '',
              }))
              .filter(l => l.symbol);
            next[f.id] = {
              status: String(d.status || ''), mode: String(d.tradingMode || ''),
              maxLoss: Number(d.maxLossPerDay || 0), legs, pending: _pending,
            };
          }
          setDeps(next);
        }
        // Same gate on the ledger: a KALI registry must not total PAPER trades.
        const _now = new Date().toLocaleTimeString('en-GB', { timeZone: 'Asia/Kolkata', hour12: false });
        if (Array.isArray(lRes?.trades)) {
          setRows((lRes.trades as any[]).filter((r: any) => inLane(r?.tradingMode)));
          setLedgerLoaded(true);
          setStaleSince('');
          setTs(_now);
        } else {
          setStaleSince(prev => prev || _now);
        }
      } catch { /* poll again */ }
    };
    load();
    const id = setInterval(() => { if (!document.hidden) load(); }, 15000);
    return () => { dead = true; clearInterval(id); };
  }, [lane]);   // refetch when the lane changes


  // Live M2M — polled faster than the ledger because it is the only number on
  // this card that moves tick by tick. Bridge unreachable stays HONEST: the
  // cells read "…" rather than falling back to a stale or invented figure.
  useEffect(() => {
    let dead = false;
    let lastOkAt = 0;
    // A failed pull keeps the last good M2M for at most 20 s, labelled with its
    // age; past that the cells go back to "unreachable" rather than show an old
    // number as if it were live.
    const fail = () => {
      if (dead) return;
      setPosOk(false);
      const age = lastOkAt ? Math.round((Date.now() - lastOkAt) / 1000) : 0;
      setPosAgeS(age);
      if (!lastOkAt || age > 20) setLive({});
    };
    const pull = async () => {
      try {
        const r = await fetch(`${POSITIONS_URL}?t=${Date.now()}`,
          { cache: 'no-store', signal: AbortSignal.timeout(10000) });
        const d = await r.json();
        if (dead) return;
        if (r.ok && d?.ok) {
          const next: Record<string, { ltp: number | null; pnl: number }> = {};
          for (const l of (d.legs || [])) {
            if (l?.id && l.pnl != null) next[String(l.id)] = { ltp: l.ltp ?? null, pnl: Number(l.pnl) };
          }
          setLive(next); setPosOk(true); setPosAgeS(0); lastOkAt = Date.now();
        } else { fail(); }
      } catch { fail(); }
    };
    pull();
    const iv = setInterval(() => { if (!document.hidden) pull(); }, 5000);
    return () => { dead = true; clearInterval(iv); };
  }, [lane]);   // refetch when the lane changes


  // ── the aggregation: bot → symbol → TF ───────────────────────────────────
  const stats = useMemo(() => {
    const today = todayIst();
    const acc: Record<string, BotStats> = {};
    for (const f of FLEET) acc[f.id] = { ...emptyCell(), bySymbol: {} };

    for (const r of rows) {
      // VOID rows are quarantined trades — a fabricated price, a probe, a
      // protective reversal. God 2026-08-24: this card kept showing GOLD
      // −₹7,85,353 hours after those fills were voided, while every other panel
      // showed +54,958, because it read `netPnl` and voiding had only zeroed
      // `pnl`. Skipping the row is the fix that survives a void which forgets
      // to zero some other field.
      if (String(r.status || '').toUpperCase() === 'VOID' || r.voided) continue;

      // A row is in scope if it is TODAY'S, or if it is still open — whatever
      // day it was entered (God 2026-08-26).
      //
      // `openAlways=1` exists precisely to append OPEN/PARTIALLY_CLOSED rows from
      // outside the date window (server.cjs: "Shool positional holds for days"),
      // and `ledger_open_positions()` sends exactly those legs to the positions
      // feed. Filtering on `dateIso === today` first threw them straight back
      // away: a leg entered yesterday and still running showed a live M2M in the
      // Open Positions panel and NOTHING here — no green row, no M2M, and it was
      // missing from the fleet's open count. That is the one disagreement this
      // card is built to make impossible, so scope is widened rather than the
      // parameter dropped.
      const isToday = String(r.dateIso || '') === today;
      // Mirrors the server's OWN open-leg filter (bhuvaneshwari_data
      // `ledger_open_positions`: status + openQty > 0, voided already skipped
      // above) so this card and the Open Positions panel count the same legs.
      const stillOpen = ['OPEN', 'PARTIALLY_CLOSED'].includes(String(r.status || '').toUpperCase())
        && Number(r.openQty ?? 0) > 0;
      if (!isToday && !stillOpen) continue;

      const f = FLEET.find(x => x.id === String(r.botId || '') || x.legacy === String(r.botId || ''));
      if (!f) continue;

      const sym = canonSym(r.symbol || r.optionSymbol) || '?';
      const tf = String(r.timeframe || '—');
      const pnl = Number(r.netPnl ?? r.pnl ?? 0) || 0;

      const b = acc[f.id];
      const s = (b.bySymbol[sym] ||= { ...emptyCell(), byTf: {} });
      const c = (s.byTf[tf] ||= emptyCell());

      // Realised P&L, trade counts and the average buy/sell prices stay strictly
      // TODAY-only — they are the day's numbers, and a carried leg's entry fill
      // happened on a different day's average. The carried row still reaches the
      // cells above, so the leg is VISIBLE and its M2M counts; it simply adds no
      // realised history to today.
      if (isToday) {
        addRow(b as any, r, pnl);
        addRow(s as any, r, pnl);
        addRow(c as any, r, pnl);
      }

      // Live M2M rides the JOIN: the positions feed knows this leg by its trade
      // id but not its timeframe, and the ledger row knows both. The P&L value
      // itself is the server's, carried across untouched.
      // Openness comes from the LEDGER; only the M2M number comes from the feed.
      // Gating the open-count on `lv` meant that whenever the bridge could not
      // price a leg the position vanished from this card altogether — no green
      // row, no OPEN badge, no auto-expand — which is exactly the moment an admin
      // most needs to see it. Today's screenshot showed 13 open positions on the
      // right and not one green row here, for that reason.
      if (stillOpen) {
        const lv = live[String(r.id || '')];
        for (const t of [b, s, c]) {
          t.openLegs += 1;
          if (!isToday) t.carried += 1;
          if (lv) { t.liveM2M = (t.liveM2M ?? 0) + lv.pnl; t.pricedLegs += 1; }
        }
      }
    }
    for (const b of Object.values(acc)) {
      finalise(b as any);
      for (const s of Object.values(b.bySymbol)) {
        finalise(s as any);
        for (const c of Object.values(s.byTf)) finalise(c as any);
      }
    }
    return acc;
  }, [rows, live]);

  // Deployed legs that have NOT traded today still deserve a row — a symbol
  // that is armed and silent is exactly what an admin needs to see. They are
  // merged in with zeroed stats rather than hidden.
  const symbolsOf = useMemo(() => {
    const out: Record<string, { sym: string; tfs: string[]; st: SymStats; cells: Record<string, CellState>; on: number }[]> = {};
    for (const f of FLEET) {
      const traded = stats[f.id]?.bySymbol || {};
      const merged: Record<string, Set<string>> = {};
      const cellsOf: Record<string, Record<string, CellState>> = {};
      for (const l of (deps[f.id]?.legs || [])) {
        (merged[l.symbol] ||= new Set()).add(l.tf || '—');
        const c = ((cellsOf[l.symbol] ||= {})[l.tf || '—'] ||= { on: false, pine: '' });
        c.on = c.on || l.enabled;
        c.pine = c.pine || l.pine;
      }
      for (const [s, sv] of Object.entries(traded)) {
        for (const tf of Object.keys(sv.byTf)) (merged[s] ||= new Set()).add(tf);
      }
      out[f.id] = Object.entries(merged)
        .map(([sym, tfset]) => ({
          sym,
          tfs: [...tfset].sort((a, b) => tfRank(a) - tfRank(b)),
          st: traded[sym] || { ...emptyCell(), byTf: {} },
          cells: cellsOf[sym] || {},
          on: Object.values(cellsOf[sym] || {}).filter(c => c.on).length,
        }))
        // Anything with an open position first, then by how much it moved
        // today, then alphabetically so the order is stable between polls.
        .sort((a, b) =>
          (b.st.openLegs - a.st.openLegs) ||
          (Math.abs(b.st.pnl) - Math.abs(a.st.pnl)) ||
          a.sym.localeCompare(b.sym));
    }
    return out;
  }, [stats, deps]);

  const fleetToday = useMemo(
    () => FLEET.reduce((s, f) => s + (stats[f.id]?.pnl || 0), 0), [stats]);
  const fleetLive = useMemo(
    () => FLEET.reduce((s, f) => s + (stats[f.id]?.liveM2M ?? 0), 0), [stats]);
  const fleetOpen = useMemo(
    () => FLEET.reduce((s, f) => s + (stats[f.id]?.openLegs || 0), 0), [stats]);
  const fleetPriced = useMemo(
    () => FLEET.reduce((s, f) => s + (stats[f.id]?.pricedLegs || 0), 0), [stats]);

  return (
    <div style={{ marginBottom: 12 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8, padding: '2px 4px 10px' }}>
        <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: 1, color: '#94a3b8' }}>
          🛡️ MASTER FLEET REGISTRY <span style={{ color: '#475569', fontWeight: 600 }}>· auto-synced from server{ts ? ` · ${ts}` : ''}</span>
          {staleSince && <span title="last poll failed - showing the last good server data"
            style={{ marginLeft: 6, color: '#f59e0b', fontWeight: 800 }}>· STALE since {staleSince}</span>}
        </div>
        <div style={{ display: 'flex', gap: 14, alignItems: 'baseline' }}>
          {fleetOpen > 0 && (
            <div style={{ fontSize: 11, fontWeight: 800, color: fleetPriced > 0 ? pnlColor(fleetLive) : MUT, fontVariantNumeric: 'tabular-nums' }}>
              RUNNING M2M: {fleetPriced > 0 ? fmt(fleetLive) : '⋯'}
              <span style={{ color: '#475569', fontWeight: 600 }}> · {fleetOpen} open</span>
            </div>
          )}
          <div style={{ fontSize: 12, fontWeight: 900, color: pnlColor(fleetToday), fontVariantNumeric: 'tabular-nums' }}>
            FLEET TODAY: {fmt(fleetToday)}
          </div>
        </div>
      </div>

      {/* EMPTY IS NOW A DIFFERENT FACT (God 2026-08-28). This used to fire on
          KALI constantly, because the lane gate dropped every non-LIVE bot and
          the fleet is 100% paper. KALI now lists the whole fleet with a PENDING
          badge, so an empty KALI registry means the DEPLOYMENTS FETCH returned
          nothing — a real problem — not "no LIVE bots yet". The condition is
          therefore whether any bot loaded at all, not whether any is LIVE. */}
      {FLEET.every(f => !deps[f.id]) && (
        <div style={{
          padding: '14px 16px', borderRadius: 8, fontSize: 11, lineHeight: 1.6,
          background: lane === 'kali' ? 'rgba(239,68,68,.07)' : 'rgba(56,189,248,.07)',
          border: `1px solid ${lane === 'kali' ? 'rgba(239,68,68,.35)' : 'rgba(56,189,248,.3)'}`,
          color: 'var(--text-muted,#94a3b8)',
        }}>
          <b style={{ color: lane === 'kali' ? '#ef4444' : '#38bdf8' }}>
            {lane === 'kali' ? '🔱 KALI — registry khaali hai' : '🧪 DUMABATI — koi PAPER bot nahi'}
          </b>
          <div style={{ marginTop: 4 }}>
            {lane === 'kali'
              ? <>KALI ab <b>poori fleet</b> dikhati hai — jo bot abhi live-approved nahi hai wo
                  <b> ⏳ WAITING FOR PINE V6 APPROVAL</b> ke saath listed rehta hai. Isliye is panel ka
                  khaali hona ka matlab hai <b>deployments load hi nahi hue</b> — ye dekhne wali baat hai,
                  normal nahi.</>
              : <>Ye registry sirf <b>PAPER</b> bots dikhati hai. Baaki bots
                  <b> LIVE AUTO TRADING KALI</b> par hain — kuch chhupa ya kho nahi hai.</>}
          </div>
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 340px), 1fr))', gap: 10, alignItems: 'start' }}>
        {FLEET.map(f => {
          const d = deps[f.id];
          // LANE GATE ON THE RENDER, not just on the fetch (God 2026-08-26).
          // FLEET is a hardcoded seven-bot identity list, so this loop draws
          // every bot unconditionally and `deps` only enriches it. Filtering the
          // fetch alone therefore removed the DATA but left the CARDS — which is
          // why the KALI page still showed all seven bots with RUNNING/PAPER
          // badges after the first fix. The card itself has to be gated.
          //
          // ASYMMETRIC ON PURPOSE. inLane() reads an absent mode as PAPER, so on
          // DUMABATI a bot whose deployment has not loaded yet still draws
          // (no regression during an API hiccup), while on KALI it does not —
          // the real-money page requires positive evidence that a bot is LIVE.
          // A bot with no deployment loaded still draws on DUMABATI (no blank
          // grid during an API hiccup) and still does not on KALI — the
          // real-money page requires positive evidence a bot EXISTS before it
          // claims one. What changed is that a bot which exists but is not
          // LIVE-approved is now LISTED and marked, instead of vanishing.
          if (!d && lane === 'kali') return null;
          if (!inLane(d?.mode) && lane !== 'kali') return null;
          const b = stats[f.id];
          const pending = !!d?.pending;
          // A pending bot is NOT running here, whatever its paper status says.
          const running = d?.status === 'RUNNING' && !pending;
          const liveMode = (d?.mode || '').toUpperCase() === 'LIVE';
          const symRows = symbolsOf[f.id] || [];
          const priced = (b?.pricedLegs || 0) > 0;
          const m2mCol = priced ? pnlColor(b?.liveM2M ?? 0) : MUT;
          const cardOpen = openCard.has(f.id);
          return (
            /* 2026 glass card (God 2026-09-04). The bot's accent no longer
               fills a flat plate — it is a luminous top edge over glass, and a
               RUNNING bot gets a faint aura in its own colour so a live fleet
               reads at a glance without counting badges. */
            <div key={f.id} className="jc-glass jc-hoverable" style={{
              borderTop: `2px solid ${f.accent}`,
              borderTopLeftRadius: 'var(--jc-r-md)', borderTopRightRadius: 'var(--jc-r-md)',
              padding: '12px 14px 10px', minWidth: 0,
              boxShadow: running
                ? `var(--jc-shadow-2), 0 0 22px ${f.accent}22`
                : 'var(--jc-shadow-2)',
            }}>
              {/* Card head: identity + state, and the handle for the whole
                  symbol list. Nothing in this row performs a trade action — no
                  EXIT, no STOP, status is not editable here — so the entire row
                  is safe to make clickable, which gives a much bigger target
                  than a caret alone.

                  It WRAPS on purpose: "Rudra Vega Max Sell" next to an "MCX OPT
                  SELL" badge does not fit a 340px card, and without wrapping the
                  badge broke across two lines and shoved the title into the
                  status dot. */}
              {/* The handle covers the head AND the hero, because the words that
                  invite the click — "N symbols · click to view" — live in the
                  hero row. Putting the handler on the head alone made the label
                  a lie: clicking it selected the text instead of opening
                  anything. An affordance has to BE the target it advertises. */}
              <div
                onClick={() => toggleCard(f.id)}
                role="button"
                tabIndex={0}
                aria-expanded={cardOpen}
                onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleCard(f.id); } }}
                title={cardOpen ? 'hide symbols' : `show ${symRows.length} symbol${symRows.length === 1 ? '' : 's'}`}
                style={{ cursor: 'pointer', userSelect: 'none' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, rowGap: 5, flexWrap: 'wrap', marginBottom: 10 }}>
                <span style={{ fontSize: 10, lineHeight: 1, flexShrink: 0, color: cardOpen ? '#7dd3fc' : '#64748b',
                               display: 'inline-block', width: 10, textAlign: 'center',
                               transition: 'transform .12s ease', transform: cardOpen ? 'rotate(90deg)' : 'none' }}>▶</span>
                <span style={{ fontSize: 16, lineHeight: 1, flexShrink: 0 }}>{f.icon}</span>
                <span style={{ fontSize: 13.5, fontWeight: 900, color: '#e2e8f0', letterSpacing: 0.4, whiteSpace: 'nowrap' }}>{f.name}</span>
                <span className="jc-chip" style={{ color: f.accent, borderColor: `${f.accent}55`, background: `${f.accent}14`, whiteSpace: 'nowrap', flexShrink: 0 }}>{f.segment}</span>
                <span style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
                  {/* Status pill. The dot pulses ONLY while genuinely running —
                      a badge that always breathes stops being a status. */}
                  <span className={`jc-pill ${running ? 'live' : 'off'}`}>
                    <span className="dot" />
                    {pending ? 'PENDING' : (d?.status || '…')}
                  </span>
                  {/* A pending bot must not wear a PAPER badge on the real-money
                      page — that badge, on this exact panel, was the 2026-08-26
                      defect. It says what it is waiting for instead. */}
                  <span className="jc-chip"
                        title={pending ? 'Listed on KALI. Live execution is blocked until God approves this bot’s Pine V6 code.' : undefined}
                        style={{ color: pending ? 'var(--jc-warn)' : (liveMode ? 'var(--jc-neg)' : 'var(--jc-warn)'),
                                 borderColor: `${pending ? '#ffb340' : (liveMode ? '#ff4d6a' : '#ffb340')}55`,
                                 background: pending ? 'rgba(255,179,64,.10)' : (liveMode ? 'rgba(255,77,106,.10)' : 'rgba(255,179,64,.10)'),
                                 whiteSpace: 'nowrap' }}>
                    {pending ? '⏳ WAITING FOR PINE V6 APPROVAL' : (d?.mode || 'PAPER').toUpperCase()}
                  </span>
                </span>
              </div>

              {/* hero: today's realised number, then the running M2M beside it */}
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, rowGap: 4, marginBottom: 10, flexWrap: 'wrap' }}>
                <span style={{ fontSize: 23, fontWeight: 900, lineHeight: 1.1, color: pnlColor(b?.pnl || 0), fontVariantNumeric: 'tabular-nums' }}>
                  {fmt(b?.pnl || 0)}
                </span>
                {(b?.openLegs || 0) > 0 && (
                  <span title={priced
                    ? 'mark-to-market on open legs — served by the bridge, not computed here'
                    : 'these legs are open, but the bridge has no live price for them yet'}
                    style={{ fontSize: 10.5, fontWeight: 800, color: m2mCol, border: `1px solid ${m2mCol}55`, borderRadius: 4, padding: '2px 7px', whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>
                    M2M {priced ? fmt(b?.liveM2M ?? 0) : '⋯'}
                    <span style={{ color: '#475569', fontWeight: 700 }}> · {b?.openLegs} open</span>
                  </span>
                )}
                <span style={{ fontSize: 10, color: '#64748b', whiteSpace: 'nowrap' }}>
                  {b?.trades || 0} trades · <span style={{ color: POS }}>{b?.wins || 0}W</span>/<span style={{ color: NEG }}>{b?.losses || 0}L</span>
                  {d?.maxLoss ? <> · bot kill −₹{d.maxLoss.toLocaleString('en-IN')} · cell ₹5,000</> : null}
                </span>
                <button onClick={(e) => { e.stopPropagation(); setLedgerBot({ id: f.id, name: (f as any).name || f.id }); }}
                  title="SKIPPED CENSUS LEDGER — Symbol · TF · Reason of this bot's skipped signals"
                  style={{ fontSize: 10, fontWeight: 800, color: '#fbbf24', background: 'rgba(251,191,36,.08)', border: '1px solid rgba(251,191,36,.45)',
                           borderRadius: 999, padding: '3px 10px', minHeight: 26, cursor: 'pointer', whiteSpace: 'nowrap' }}>
                  ⊘ {skipByBot[f.id] ?? 0} skipped ▸
                </button>
                {/* The count is the whole point of collapsing: a shut card must
                    still say how much is inside it, or it reads as empty. */}
                {symRows.length > 0 && (
                  <span style={{ marginLeft: 'auto', fontSize: 9.5, fontWeight: 800, color: cardOpen ? '#7dd3fc' : '#64748b', whiteSpace: 'nowrap' }}>
                    {symRows.length} symbol{symRows.length === 1 ? '' : 's'}
                    <span style={{ color: '#475569', fontWeight: 600 }}> · {symRows.reduce((n, r) => n + r.on, 0)}/{symRows.reduce((n, r) => n + Object.keys(r.cells).length, 0)} cells ON</span>
                    {!cardOpen && <span style={{ color: '#475569', fontWeight: 600 }}> · click to view</span>}
                  </span>
                )}
              </div>
              </div>

              {/* Column header. Naming the numeric columns ONCE is what lets the
                  rows below drop their inline "t" and "₹" labels and still read.
                  It sits OUTSIDE the scroll box below so it stays put while a
                  29-symbol list scrolls under it. */}
              {cardOpen && symRows.length > 0 && (
                <div style={{ ...rowBase, gridTemplateColumns: L1_COLS, cursor: 'default', padding: '0 0 4px 8px', borderBottom: '1px solid #1e293b' }}>
                  <span />
                  <span style={{ ...colHead }}>SYMBOL</span>
                  <span style={{ ...colHead, ...numCell }}>M2M</span>
                  <span style={{ ...colHead, ...numCell }}>TRD</span>
                  <span style={{ ...colHead, ...numCell }}>DAY P&L</span>
                </div>
              )}

              {/* ── L1 SYMBOLS ──────────────────────────────────────────────── */}
              {/* Capped and scrollable rather than unbounded. Sthambh's 29
                  symbols — each of which can open its own timeframes and then
                  its own contracts — would otherwise make ONE card taller than
                  the viewport and push every card after it out of the grid flow,
                  which is exactly the state this replaces. The cap belongs here
                  and not on the card, so the head and hero numbers never scroll
                  away from the list they describe. */}
              {cardOpen && (
                <div style={{ maxHeight: 340, overflowY: 'auto', overflowX: 'hidden', overscrollBehavior: 'contain' }}>
              {symRows.length === 0 && (
                <div style={{ fontSize: 10, color: '#475569', padding: '6px 2px' }}>{d ? 'no legs deployed' : depsAuth ? `login expired (HTTP ${depsAuth}) — dobara login karein` : 'syncing…'}</div>
              )}
              {symRows.map(({ sym, tfs, st, cells }) => {
                const symKey = `${f.id}|${sym}`;
                const symOpen = openSym.has(symKey);
                const hasPos = st.openLegs > 0;
                const symPriced = st.pricedLegs > 0;
                return (
                  <div key={sym} style={{ borderBottom: '1px solid #16202e' }}>
                    <button
                      onClick={() => toggle(openSym, symKey, setOpenSym)}
                      aria-expanded={symOpen}
                      title={`${sym} — ${tfs.length} timeframe${tfs.length === 1 ? '' : 's'} deployed`}
                      style={{
                        ...rowBase, gridTemplateColumns: L1_COLS,
                        padding: '7px 0 7px 6px',
                        background: hasPos ? '#10b9810f' : 'transparent',
                        borderLeft: `2px solid ${hasPos ? '#10b981' : 'transparent'}`,
                      }}>
                      <Caret open={symOpen} />
                      <span style={{ ...nameCell, fontSize: 11.5, fontWeight: 700, color: '#e2e8f0' }}>
                        <span style={ellip}>{sym}</span>
                        {lane === 'paper' && paperLots[normSym(sym)]?.lots ? (
                          <span title={`Global paper lots: ${paperLots[normSym(sym)].lots} x lot ${paperLots[normSym(sym)].lot ?? '?'} (Funds Hub)`}
                            style={{ fontSize: 8.5, fontWeight: 800, color: '#c4b5fd', border: '1px solid #7c3aed66', borderRadius: 3, padding: '1px 4px', flexShrink: 0 }}>
                            {paperLots[normSym(sym)].lots}L
                          </span>
                        ) : null}
                        {hasPos && <span className="jc-live-dot" style={{ width: 5, height: 5, borderRadius: '50%', background: '#10b981', flexShrink: 0 }} />}
                      </span>
                      <span style={{ ...numCell, fontSize: 10.5, fontWeight: 800, color: hasPos ? (symPriced ? pnlColor(st.liveM2M ?? 0) : MUT) : '#334155' }}>
                        {hasPos ? (symPriced ? fmt(st.liveM2M ?? 0) : '⋯') : ''}
                      </span>
                      <span style={{ ...numCell, fontSize: 10, color: st.trades ? '#94a3b8' : '#334155' }}>
                        {st.trades || '—'}
                      </span>
                      <span style={{ ...numCell, fontSize: 11.5, fontWeight: 800, color: pnlColor(st.pnl) }}>{fmt(st.pnl)}</span>
                    </button>

                    {/* ── L2 TIMEFRAMES ───────────────────────────────────────── */}
                    {symOpen && (
                      <div style={{ marginLeft: 13, borderLeft: '1px solid #1e293b', padding: '2px 0 5px' }}>
                        {tfs.map(tf => {
                          const tfKey = `${symKey}|${tf}`;
                          const tfOpen = openTf.has(tfKey);
                          const c = st.byTf[tf] || emptyCell();
                          const idle = c.trades === 0 && c.openLegs === 0;
                          const tfPriced = c.pricedLegs > 0;
                          return (
                            <div key={tf}>
                              <button
                                onClick={() => toggle(openTf, tfKey, setOpenTf)}
                                aria-expanded={tfOpen}
                                title={idle ? `${sym} ${tf} — deployed, no trade today` : `${sym} ${tf} — open details`}
                                style={{
                                  ...rowBase, gridTemplateColumns: L2_COLS,
                                  padding: '6px 0 6px 9px', opacity: idle ? 0.45 : 1,
                                }}>
                                <Caret open={tfOpen} size={8} />
                                <span style={{ ...nameCell, gap: 6 }}>
                                  <span style={{ fontSize: 10.5, fontWeight: 800, color: '#7dd3fc', letterSpacing: 0.3 }}>{tf}</span>
                                  {cells[tf] && (
                                    <span title={cells[tf].on ? 'cell ARMED (leg ON)' : 'cell DISARMED (leg OFF) - no new entries'}
                                      style={{ fontSize: 8, fontWeight: 800, color: cells[tf].on ? '#10b981' : '#64748b', border: `1px solid ${cells[tf].on ? '#10b98155' : '#33415588'}`, borderRadius: 3, padding: '1px 4px', whiteSpace: 'nowrap', flexShrink: 0 }}>
                                      {cells[tf].on ? 'ON' : 'OFF'}
                                    </span>
                                  )}
                                  {cells[tf] && (
                                    <span title={cells[tf].pine ? `pinned Pine ${cells[tf].pine}` : 'no Pine v6 pinned yet (RULE #94 step 3)'}
                                      style={{ fontSize: 8, fontWeight: 800, color: cells[tf].pine ? '#c4b5fd' : '#f59e0b', border: `1px solid ${cells[tf].pine ? '#7c3aed66' : '#f59e0b55'}`, borderRadius: 3, padding: '1px 4px', whiteSpace: 'nowrap', flexShrink: 0 }}>
                                      {cells[tf].pine ? `PINE ${cells[tf].pine}` : 'NO PINE'}
                                    </span>
                                  )}
                                  {c.openLegs > 0 && (
                                    <span style={{ fontSize: 8, fontWeight: 800, color: '#10b981', border: '1px solid #10b98155', borderRadius: 3, padding: '1px 4px', whiteSpace: 'nowrap', flexShrink: 0 }}>
                                      {c.openLegs} OPEN
                                    </span>
                                  )}
                                </span>
                                <span style={{ ...numCell, fontSize: 10, fontWeight: 800, color: c.openLegs ? (tfPriced ? pnlColor(c.liveM2M ?? 0) : MUT) : '#334155' }}>
                                  {c.openLegs ? (tfPriced ? fmt(c.liveM2M ?? 0) : '⋯') : ''}
                                </span>
                                <span style={{ ...numCell, fontSize: 9.5, color: c.trades ? '#94a3b8' : '#334155' }}>
                                  {c.trades || '—'}
                                </span>
                                <span style={{ ...numCell, fontSize: 10.5, fontWeight: 800, color: pnlColor(c.pnl) }}>{fmt(c.pnl)}</span>
                              </button>

                              {/* ── L3 METRICS ──────────────────────────────── */}
                              {tfOpen && (
                                <div style={{
                                  display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(108px, 1fr))',
                                  gap: 12, margin: '3px 2px 8px 20px', padding: '11px 13px',
                                  background: '#0b1220', border: '1px solid #1e293b', borderRadius: 8,
                                }}>
                                  <Metric label="TOTAL TRADES"
                                    value={String(c.trades)}
                                    sub={c.trades
                                      ? `${c.wins}W / ${c.losses}L${c.trades - c.wins - c.losses > 0 ? ` / ${c.trades - c.wins - c.losses}BE` : ''}`
                                      : 'none today'} />
                                  <Metric label="LIVE M2M"
                                    value={c.openLegs === 0 ? '—' : tfPriced ? fmt(c.liveM2M ?? 0) : '⋯'}
                                    color={c.openLegs === 0 || !tfPriced ? MUT : pnlColor(c.liveM2M ?? 0)}
                                    sub={c.openLegs === 0 ? 'no open leg'
                                      : !tfPriced ? (posOk === false ? 'bridge unreachable' : 'awaiting price')
                                      : posOk === false ? `stale ${posAgeS}s · bridge unreachable`
                                      : `${c.openLegs} running${c.carried ? ` · ${c.carried} carried` : ''}${c.openLegs > c.pricedLegs ? ` · ${c.openLegs - c.pricedLegs} unpriced` : ''}`} />
                                  <Metric label="AVG BUY"
                                    value={px(c.avgBuy)}
                                    sub={c.buyQty ? `qty ${c.buyQty.toLocaleString('en-IN')}`
                                      : !ledgerLoaded ? 'ledger not loaded yet'
                                      : c.trades ? 'trades counted but no priced fill'
                                      : c.carried ? 'entered an earlier day'
                                      : 'no buy fill'} />
                                  <Metric label="AVG SELL"
                                    value={px(c.avgSell)}
                                    sub={c.sellQty ? `qty ${c.sellQty.toLocaleString('en-IN')}`
                                      : !ledgerLoaded ? 'ledger not loaded yet'
                                      : c.openLegs ? 'still open'
                                      : c.trades ? 'trades counted but no priced fill'
                                      : 'no sell fill'} />
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
                </div>
              )}
            </div>
          );
        })}
      </div>
      {ledgerBot && (
        <Suspense fallback={null}>
          <SkippedCensusLedger lane={lane} date={sessionIso()} bot={ledgerBot.id} botName={ledgerBot.name}
            isMobile={typeof window !== 'undefined' && window.innerWidth < 768} onClose={() => setLedgerBot(null)} />
        </Suspense>
      )}
    </div>
  );
};

// ── row geometry ───────────────────────────────────────────────────────────
// ONE grid template per level, with the three numeric columns at FIXED width.
// That is what actually aligns the numbers. These rows used to right-align a
// flex cluster instead, which only LOOKS like a column while every label happens
// to be the same width — "4t · −₹2,94,218" and "11t · ₹0" put their digits at
// different x-positions on consecutive rows, and the card read as misaligned.
//
// Indentation moves the LEFT edge only. Because the numeric columns are fixed
// and every level shares one right edge, a timeframe's numbers sit directly
// under its symbol's — the hierarchy stays legible down the column, not just
// across the row.
const M2M_W = 72, TRD_W = 34, PNL_W = 94;
const L1_COLS = `12px minmax(0,1fr) ${M2M_W}px ${TRD_W}px ${PNL_W}px`;
const L2_COLS = `10px minmax(0,1fr) ${M2M_W}px ${TRD_W}px ${PNL_W}px`;

const rowBase: React.CSSProperties = {
  width: '100%', display: 'grid', alignItems: 'center', columnGap: 9,
  background: 'transparent', border: 'none', textAlign: 'left',
  color: 'inherit', font: 'inherit', cursor: 'pointer',
};
const numCell: React.CSSProperties = {
  textAlign: 'right', whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums',
};
const nameCell: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 5, minWidth: 0,
};
const ellip: React.CSSProperties = {
  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0,
};
const colHead: React.CSSProperties = {
  fontSize: 8, fontWeight: 800, letterSpacing: 0.8, color: '#475569',
};

const Caret: React.FC<{ open: boolean; size?: number }> = ({ open, size = 9 }) => (
  <span aria-hidden style={{
    fontSize: size, lineHeight: 1, color: open ? '#7dd3fc' : '#475569',
    display: 'inline-block', textAlign: 'center',
    transition: 'transform .12s ease', transform: open ? 'rotate(90deg)' : 'none',
  }}>▶</span>
);

const Metric: React.FC<{ label: string; value: string; sub?: string; color?: string }> =
  ({ label, value, sub, color }) => (
    <div style={{ minWidth: 0 }}>
      <div style={{ fontSize: 8, fontWeight: 800, letterSpacing: 0.7, color: '#475569', whiteSpace: 'nowrap', marginBottom: 3 }}>{label}</div>
      <div style={{ fontSize: 13.5, fontWeight: 800, lineHeight: 1.2, color: color || '#e2e8f0', fontVariantNumeric: 'tabular-nums', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{value}</div>
      {sub && <div style={{ fontSize: 8.5, color: '#475569', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', marginTop: 2 }}>{sub}</div>}
    </div>
  );

export default React.memo(FleetRegistryCards);
